import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import UserPointsManager from '../../components/Admin/UserPointsManager';
import AdminReports from '../../components/Admin/AdminReports';
import BarAdmissionsManager from '../../components/Admin/BarAdmissionsManager';
import Navbar from '../../components/Navbar/Navbar';
import { ShieldCheck, Gavel, AlertTriangle, Scale } from 'lucide-react';
import './AdminPage.css';

const AdminPage = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('admissions');
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        if (!auth.currentUser) {
          navigate('/login');
          return;
        }

        const userDocRef = doc(db, 'users', auth.currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists() && userDoc.data().isAdmin === true) {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [navigate]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Entering Chief Justice Chambers...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-denied-wrapper">
        <Navbar />
        <div className="admin-page-container">
          <div className="admin-denied-card">
            <Scale size={48} className="denied-icon" />
            <h1>Chamber Access Denied</h1>
            <p>You do not possess Chief Justice credentials to enter this judicial chamber.</p>
            <span className="denied-user-info">Logged in as: {auth.currentUser?.email || 'Anonymous'}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <Navbar />
      <div className="admin-page-container">
        <aside className="admin-sidebar">
          <div className="admin-sidebar-header">
            <Scale className="sidebar-logo-icon" size={22} />
            <div>
              <h2>Chief Justice</h2>
              <span className="sidebar-subtext">Judicial High Panel</span>
            </div>
          </div>
          <ul className="admin-menu">
            <li 
              className={activeSection === 'admissions' ? 'active' : ''}
              onClick={() => handleSectionChange('admissions')}
            >
              <ShieldCheck size={18} />
              <span>Bar Admissions</span>
            </li>
            <li 
              className={activeSection === 'user' ? 'active' : ''}
              onClick={() => handleSectionChange('user')}
            >
              <Gavel size={18} />
              <span>Chips & Sanctions</span>
            </li>
            <li 
              className={activeSection === 'reports' ? 'active' : ''}
              onClick={() => handleSectionChange('reports')}
            >
              <AlertTriangle size={18} />
              <span>Dispute Reports</span>
            </li>
          </ul>
        </aside>

        <main className="admin-content">
          <div className="admin-content-header">
            <h1>Chief Justice Chambers</h1>
            <p className="admin-content-subtext">Judicial administration, jury oversight, and peer discipline governance.</p>
          </div>
          
          {activeSection === 'admissions' && (
            <div className="admin-section-card">
              <BarAdmissionsManager />
            </div>
          )}

          {activeSection === 'user' && (
            <div className="admin-section-card">
              <UserPointsManager />
            </div>
          )}
          
          {activeSection === 'reports' && (
            <div className="admin-section-card">
              <AdminReports />
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminPage;